/* tslint:disable */
/* eslint-disable */
/**
 * Joule Conductor Cloud
 * This specification document describes the Joule Technologies Application Programming Interface  (API). The endpoints, message formats and status codes are explained in this document. ## Communication  ### Base URI  The base Uniform Resource Identifier (URI) is: https://user-api.joule-cloud.com/v2 This URI is the prefix for all endpoints described in this document. ### Body format  The endpoints of the API expecting a body must be in JavaScript Object Notation (JSON) format.  Response bodies from the endpoints also adhere to this standard, unless otherwise stated.  Responses will include a Content-Type header indicating the ‘application/json’ type. An invalid  or missing body will result in a 400-status code accompanied by an error code to specify the  nature of the error. ### Security The API uses HTTPS as the underlying protocol to transfer data. The server hosts a certificate  to set up a secure connection using Transport Layer Security (TLS) version 1.2. The authenticity  of the server certificate is verifiable by the client using publicly available sources.  #### Authentication and authorization  The API should only be called by legitimate users with the correct access rights. The API uses  the Open Authorization 2.0 (OAuth) standard to achieve this. Every request to the API should  include a bearer access token in the JSON Web Token (JWT) format in the Authorization header.  The header should be formatted such as in the example below.  ```   {“Authorization”: “Bearer <JWT>”}  ``` The JWT must be acquired from one of the following identity providers:   * joule-technologies.eu.auth0.com    * inventum-app.eu.auth0.com    * joule-app.eu.auth0.com   For help regarding gaining an access token you can find the docs for Auth0  [here](https://auth0.com/docs/secure/tokens/access-tokens/get-access-tokens). The JWT must be intended for one of the following audiences:    * https://user-api.joule-cloud.com   If the authorization header does not match the requirements stated above the API will respond  with a 401-status code accompanied by an error code to specify the nature of the error.  ### Timeout The API specifies a maximum amount of time between a request and the response to avoid blocking  either side. The general timeout that has been set for each endpoint is 3000 milliseconds.  This timeout is used unless otherwise stated in the respective endpoint documentation.  If the server is unable to respond within this maximum time, the response contains a 504-status  code. The client may retry the request. The time out for a stream to be open is 1800 seconds.  If the stream is still open passed the experitation the server will close the stream. ### Rate limits  No rate limit is currently imposed. ### Diagnostics response  The diagnostics response provides the caller of the API with more information in case of a  failed request. This information can be used for debugging and recovery/retrying purposes.  The diagnostics response is always included under the “diagnostics” key if the response  has a body. 
 *
 * The version of the OpenAPI document: 2.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { UpdateStatus } from './UpdateStatus';
import {
    UpdateStatusFromJSON,
    UpdateStatusFromJSONTyped,
    UpdateStatusToJSON,
    UpdateStatusToJSONTyped,
} from './UpdateStatus';
import type { BoardVersion } from './BoardVersion';
import {
    BoardVersionFromJSON,
    BoardVersionFromJSONTyped,
    BoardVersionToJSON,
    BoardVersionToJSONTyped,
} from './BoardVersion';
import type { Device } from './Device';
import {
    DeviceFromJSON,
    DeviceFromJSONTyped,
    DeviceToJSON,
    DeviceToJSONTyped,
} from './Device';
import type { FirmwareResponse } from './FirmwareResponse';
import {
    FirmwareResponseFromJSON,
    FirmwareResponseFromJSONTyped,
    FirmwareResponseToJSON,
    FirmwareResponseToJSONTyped,
} from './FirmwareResponse';

/**
 * 
 * @export
 * @interface ManagementGetDevice200Response
 */
export interface ManagementGetDevice200Response {
    /**
     * 
     * @type {Array<Device>}
     * @memberof ManagementGetDevice200Response
     */
    attachedDevices?: Array<Device>;
    /**
     * The firmware versions of the boards announced on connect.
     * @type {Array<BoardVersion>}
     * @memberof ManagementGetDevice200Response
     */
    boardVersions?: Array<BoardVersion>;
    /**
     * 
     * @type {boolean}
     * @memberof ManagementGetDevice200Response
     */
    connected?: boolean;
    /**
     * 
     * @type {Date}
     * @memberof ManagementGetDevice200Response
     */
    createdAt?: Date;
    /**
     * The firmware version the device announced on connect.
     * @type {string}
     * @memberof ManagementGetDevice200Response
     */
    currentVersion?: string;
    /**
     * 
     * @type {string}
     * @memberof ManagementGetDevice200Response
     */
    deviceId?: string;
    /**
     * 
     * @type {string}
     * @memberof ManagementGetDevice200Response
     */
    disconnectReason?: string;
    /**
     * 
     * @type {string}
     * @memberof ManagementGetDevice200Response
     */
    displayName?: string;
    /**
     * The name of the group of the device or the firmware version.
     * @type {string}
     * @memberof ManagementGetDevice200Response
     */
    firmwareName?: string;
    /**
     * The highest currently active error for this device.
     * @type {ManagementGetDevice200ResponseHighestErrorLevelEnum}
     * @memberof ManagementGetDevice200Response
     */
    highestErrorLevel?: ManagementGetDevice200ResponseHighestErrorLevelEnum;
    /**
     * 
     * @type {string}
     * @memberof ManagementGetDevice200Response
     */
    pn?: string;
    /**
     * 
     * @type {FirmwareResponse}
     * @memberof ManagementGetDevice200Response
     */
    setFirmware?: FirmwareResponse;
    /**
     * The firmware version the device is set to.
     * @type {string}
     * @memberof ManagementGetDevice200Response
     */
    setVersion?: string;
    /**
     * 
     * @type {string}
     * @memberof ManagementGetDevice200Response
     */
    sn?: string;
    /**
     * 
     * @type {string}
     * @memberof ManagementGetDevice200Response
     */
    systemId?: string | null;
    /**
     * 
     * @type {string}
     * @memberof ManagementGetDevice200Response
     */
    type?: string;
    /**
     * 
     * @type {Date}
     * @memberof ManagementGetDevice200Response
     */
    upSince?: Date;
    /**
     * 
     * @type {UpdateStatus}
     * @memberof ManagementGetDevice200Response
     */
    updateStatus?: UpdateStatus;
    /**
     * 
     * @type {Date}
     * @memberof ManagementGetDevice200Response
     */
    lastConnection?: Date;
    /**
     * 
     * @type {string}
     * @memberof ManagementGetDevice200Response
     */
    remarks?: string | null;
    /**
     * 
     * @type {Date}
     * @memberof ManagementGetDevice200Response
     */
    updatedAt?: Date;
}


/**
 * @export
 */
export const ManagementGetDevice200ResponseHighestErrorLevelEnum = {
    Empty: '',
    Warning: 'warning',
    Error: 'error',
    ServiceRequired: 'service_required'
} as const;
export type ManagementGetDevice200ResponseHighestErrorLevelEnum = typeof ManagementGetDevice200ResponseHighestErrorLevelEnum[keyof typeof ManagementGetDevice200ResponseHighestErrorLevelEnum];


/**
 * Check if a given object implements the ManagementGetDevice200Response interface.
 */
export function instanceOfManagementGetDevice200Response(value: object): value is ManagementGetDevice200Response {
    return true;
}

export function ManagementGetDevice200ResponseFromJSON(json: any): ManagementGetDevice200Response {
    return ManagementGetDevice200ResponseFromJSONTyped(json, false);
}

export function ManagementGetDevice200ResponseFromJSONTyped(json: any, ignoreDiscriminator: boolean): ManagementGetDevice200Response {
    if (json == null) {
        return json;
    }
    return {
        
        'attachedDevices': json['attached_devices'] == null ? undefined : ((json['attached_devices'] as Array<any>).map(DeviceFromJSON)),
        'boardVersions': json['board_versions'] == null ? undefined : ((json['board_versions'] as Array<any>).map(BoardVersionFromJSON)),
        'connected': json['connected'] == null ? undefined : json['connected'],
        'createdAt': json['created_at'] == null ? undefined : (new Date(json['created_at'])),
        'currentVersion': json['current_version'] == null ? undefined : json['current_version'],
        'deviceId': json['device_id'] == null ? undefined : json['device_id'],
        'disconnectReason': json['disconnect_reason'] == null ? undefined : json['disconnect_reason'],
        'displayName': json['display_name'] == null ? undefined : json['display_name'],
        'firmwareName': json['firmware_name'] == null ? undefined : json['firmware_name'],
        'highestErrorLevel': json['highest_error_level'] == null ? undefined : json['highest_error_level'],
        'pn': json['pn'] == null ? undefined : json['pn'],
        'setFirmware': json['set_firmware'] == null ? undefined : FirmwareResponseFromJSON(json['set_firmware']),
        'setVersion': json['set_version'] == null ? undefined : json['set_version'],
        'sn': json['sn'] == null ? undefined : json['sn'],
        'systemId': json['system_id'] == null ? undefined : json['system_id'],
        'type': json['type'] == null ? undefined : json['type'],
        'upSince': json['up_since'] == null ? undefined : (new Date(json['up_since'])),
        'updateStatus': json['update_status'] == null ? undefined : UpdateStatusFromJSON(json['update_status']),
        'lastConnection': json['last_connection'] == null ? undefined : (new Date(json['last_connection'])),
        'remarks': json['remarks'] == null ? undefined : json['remarks'],
        'updatedAt': json['updated_at'] == null ? undefined : (new Date(json['updated_at'])),
    };
}

export function ManagementGetDevice200ResponseToJSON(json: any): ManagementGetDevice200Response {
    return ManagementGetDevice200ResponseToJSONTyped(json, false);
}

export function ManagementGetDevice200ResponseToJSONTyped(value?: ManagementGetDevice200Response | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'attached_devices': value['attachedDevices'] == null ? undefined : ((value['attachedDevices'] as Array<any>).map(DeviceToJSON)),
        'board_versions': value['boardVersions'] == null ? undefined : ((value['boardVersions'] as Array<any>).map(BoardVersionToJSON)),
        'connected': value['connected'],
        'created_at': value['createdAt'] == null ? value['createdAt'] : value['createdAt'].toISOString(),
        'current_version': value['currentVersion'],
        'device_id': value['deviceId'],
        'disconnect_reason': value['disconnectReason'],
        'display_name': value['displayName'],
        'firmware_name': value['firmwareName'],
        'highest_error_level': value['highestErrorLevel'],
        'pn': value['pn'],
        'set_firmware': FirmwareResponseToJSON(value['setFirmware']),
        'set_version': value['setVersion'],
        'sn': value['sn'],
        'system_id': value['systemId'],
        'type': value['type'],
        'up_since': value['upSince'] == null ? value['upSince'] : value['upSince'].toISOString(),
        'update_status': UpdateStatusToJSON(value['updateStatus']),
        'last_connection': value['lastConnection'] == null ? value['lastConnection'] : value['lastConnection'].toISOString(),
        'remarks': value['remarks'],
        'updated_at': value['updatedAt'] == null ? value['updatedAt'] : value['updatedAt'].toISOString(),
    };
}

